import React from "react";

// Status pill / tag. Theme-aware, semantic tones.

// Each tone reads from the paired --tone-*-bg / --tone-*-fg tokens in index.css,
// which flip with the theme. The old tones hardcoded light-theme inks on a
// 14%-alpha tint, so e.g. #047857 text landed on a near-black card in dark mode.
const TONES = {
  neutral: { color: "var(--text-secondary)", bg: "var(--bg-subtle)",       border: "var(--border-subtle)" },
  brand:   { color: "var(--accent-text)",    bg: "var(--accent-soft)",     border: "transparent" },
  success: { color: "var(--tone-success-fg)", bg: "var(--tone-success-bg)", border: "transparent" },
  warning: { color: "var(--tone-warning-fg)", bg: "var(--tone-warning-bg)", border: "transparent" },
  danger:  { color: "var(--tone-danger-fg)",  bg: "var(--tone-danger-bg)",  border: "transparent" },
  info:    { color: "var(--tone-info-fg)",    bg: "var(--tone-info-bg)",    border: "transparent" },
  violet:  { color: "var(--tone-violet-fg)",  bg: "var(--tone-violet-bg)",  border: "transparent" },
  orange:  { color: "var(--tone-orange-fg)",  bg: "var(--tone-orange-bg)",  border: "transparent" },
  teal:    { color: "var(--tone-teal-fg)",    bg: "var(--tone-teal-bg)",    border: "transparent" },
};

const SIZES = {
  sm: { padding: "1px 7px", fontSize: 10, height: 18 },
  md: { padding: "2px 9px", fontSize: 11, height: 22 },
  lg: { padding: "4px 10px", fontSize: 12, height: 26 },
};

export default function Pill({ tone = "neutral", size = "md", children, style = {}, ...rest }) {
  const t = TONES[tone] || TONES.neutral;
  const s = SIZES[size] || SIZES.md;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        ...s,
        borderRadius: 9999,
        fontWeight: 600,
        letterSpacing: "0.01em",
        color: t.color,
        background: t.bg,
        border: `1px solid ${t.border}`,
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {children}
    </span>
  );
}

// Convenience: known task-status pills with sensible defaults
export function StatusPill({ status }) {
  const map = {
    todo:         { tone: "neutral", label: "To do" },
    in_progress:  { tone: "info",    label: "In progress" },
    review:       { tone: "violet",  label: "In review" },
    done:         { tone: "success", label: "Done" },
    blocked:      { tone: "danger",  label: "Blocked" },
    waiting_approval: { tone: "violet", label: "Awaiting approval" },
    delivered:    { tone: "success", label: "Delivered" },
    not_started:  { tone: "neutral", label: "Not started" },
  };
  const m = map[status] || { tone: "neutral", label: status };
  return <Pill tone={m.tone}>{m.label}</Pill>;
}

// Priority indicator (dot + label)
export function PriorityPill({ priority }) {
  const map = {
    // Dot fill only — the label beside it uses --text-secondary, so these are
    // never text and carry no contrast requirement. Tokenised so the dots
    // track the theme and drop the stray Tailwind blue.
    low:    { color: "var(--text-tertiary)",   label: "Low" },
    normal: { color: "var(--fill-violet)",     label: "Normal" },
    high:   { color: "var(--fill-warning)",    label: "High" },
    urgent: { color: "var(--fill-danger)",     label: "Urgent" },
  };
  const m = map[priority] || map.normal;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text-secondary)" }}>
      <span style={{ width: 6, height: 6, borderRadius: 9999, background: m.color }} />
      {m.label}
    </span>
  );
}
